import { useState, useEffect, useMemo, useRef } from "react";
import { FiArrowLeft, FiCheck, FiInfo } from "react-icons/fi";
import { MdPayment } from "react-icons/md";
import { toast } from "react-toastify";
import html2pdf from "html2pdf.js";
import { httpService } from "../../services/httpService";
import PaymentReceipt from "../../pages/PaymentReceipt";
import config from "../../config";

const S3_BASE_URL = (
  config.S3_BASE_URL || 
  process.env.REACT_APP_S3_BASE_URL ||
  "https://flps2025.s3.ap-south-1.amazonaws.com"
).replace(/\/+$/, "");

const OrderPaymentDrawer = ({
  isOpen,
  onClose,
  orderId,
  onPaymentSuccess,
  isFullWidth = false,
  cartPayload = null,
}) => {
  // Payment states
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState({
    amount: "",
    method: "",
    reference: "",
    notes: "",
  });

  // Adjustment state
  const [adjustmentAmount, setAdjustmentAmount] = useState(0);

  // Overpayment handling
  const [overpaymentAction, setOverpaymentAction] = useState(null);
  const [showOverpaymentPrompt, setShowOverpaymentPrompt] = useState(false);

  // Receipt state
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [savedOrderForPdf, setSavedOrderForPdf] = useState(null);

  // PDF upload status
  const [pdfUploadStatus, setPdfUploadStatus] = useState(null); // 'uploading' | 'success' | 'failed'

  // Fetched order data
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [totals, setTotals] = useState({
    subtotal: 0,
    gst: 0,
    discount: 0,
    net: 0,
    paid: 0,
    remaining: 0,
  });
  const [items, setItems] = useState([]);

  // Ref to hold successful payment data until receipt is closed
  const paymentSuccessDataRef = useRef(null);

  const paymentMethodOptions = [
    { value: "cash", label: "CASH", icon: "💵" },
    { value: "upi", label: "UPI", icon: "📱" },
    { value: "card", label: "CREDIT/DEBIT CARD", icon: "💳" },
  ];

  // Fetch order when drawer opens
  useEffect(() => {
    const fetchOrder = async () => {
      if (!isOpen) return;

      // POS new-sale mode
      if (cartPayload && !orderId) {
        
        setOrderData(null);
        const orderItems = cartPayload.order_items || [];
        
        setItems(orderItems);
        setTotals({
          subtotal: cartPayload.amount_without_tax || 0,
          gst: cartPayload.total_gst || 0,
          discount: cartPayload.discount_amount || 0,
          net: cartPayload.total_amount || 0,
          paid: 0,
          remaining: cartPayload.total_amount || 0,
        });
        setAdjustmentAmount(0);
        setPaymentDetails({ amount: "", method: "", reference: "", notes: "" });
        setOverpaymentAction(null);
        setShowOverpaymentPrompt(false);
        return;
      }

      // Existing order mode
      if (!orderId) return;
      setLoadingOrder(true);
      try {
        const response = await httpService.get(`/vendororder/getvendororder/${orderId}`);
        const order = response.data;
        setOrderData(order);
        setItems(order.order_items || []);
        const subtotal = order.subtotal || order.amount_without_tax || 0;
        const gst = order.tax_total || order.total_gst || 0;
        const discountAmount = order.total_discount || 0;
        const net = order.grand_total || 0;
        const paid = order.total_paid_amount || 0;
        const remaining = net - paid;
        setTotals({
          subtotal,
          gst,
          discount: discountAmount,
          net,
          paid,
          remaining: remaining > 0 ? remaining : 0,
        });
        setAdjustmentAmount(order?.adjustment_amount || 0);
        setPaymentDetails({ amount: "", method: "", reference: "", notes: "" });
        setOverpaymentAction(null);
        setShowOverpaymentPrompt(false);
      } catch (error) {
        console.error("Failed to fetch order:", error);
        toast.error("Failed to load order details");
        onClose();
      } finally {
        setLoadingOrder(false);
      }
    };
    fetchOrder();
  }, [isOpen, orderId, cartPayload]);

  const originalDue = totals.remaining;
  const roundedDue = useMemo(() => Math.round(originalDue), [originalDue]);
  const roundOffDifference = roundedDue - originalDue;

  const effectiveDue = useMemo(() => {
    const adjustment = parseFloat(adjustmentAmount) || 0;
    let due = roundedDue - adjustment;
    return Math.max(0, due);
  }, [roundedDue, adjustmentAmount]);

  const collectedAmount = parseFloat(paymentDetails.amount) || 0;
  const isOverpayment = collectedAmount > effectiveDue && effectiveDue > 0;

  useEffect(() => {
    if (collectedAmount > effectiveDue && effectiveDue > 0) {
      setShowOverpaymentPrompt(true);
    } else {
      setShowOverpaymentPrompt(false);
      setOverpaymentAction(null);
    }
  }, [collectedAmount, effectiveDue]);

  const formatCurrency = (val, formatDecimals = 2) =>
    `₹${Number(val || 0).toFixed(formatDecimals)}`;

  const handlePaymentMethodChange = (methodValue) => {
    setPaymentDetails((prev) => ({
      ...prev,
      method: methodValue,
      reference: methodValue === "cash" ? "" : prev.reference,
      amount: effectiveDue,
    }));
  };

  // Generate PDF blob and upload
  const generateAndUploadReceiptPDF = async () => {
    const element = document.getElementById("receipt-content-pdf");
    if (!element) throw new Error("Receipt PDF element not found");

    const orderNum = savedOrderForPdf?.order_number || orderData?.order_number || "order";

    const opt = {
      margin: 0.5,
      filename: `receipt_${orderNum}.pdf`,
      image: { type: "jpeg", quality: 1 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      },
      jsPDF: {
        unit: "in",
        format: "letter",
        orientation: "portrait",
      },
    };

    const pdfBlob = await html2pdf().set(opt).from(element).output("blob");

    const formData = new FormData();
    formData.append("module", "receipts");
    formData.append("docType", "payment_receipt");
    formData.append("file", pdfBlob, `receipt_${orderNum}.pdf`);

    const response = await httpService.post("/upload/fileUpload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    const s3Key = response?.data?.s3Key || response?.data?.url;
    if (!s3Key) throw new Error("No URL returned from upload");

    return s3Key.startsWith("http")
      ? s3Key
      : `${S3_BASE_URL}/${s3Key.replace(/^\/+/, "")}`;
  };

  const submitPaymentToAPI = async (payload) => {
    try {
      if (cartPayload && !orderId) {
        const orderRes = await httpService.post(
          "/vendororder/addVendorOrderOrUpdate",
          { ...cartPayload, payment_status: payload.payment_status },
        );
        if (!orderRes?.status)
          return { success: false, error: "Failed to save order" };

        const savedOrder = orderRes.data;

        const paymentRes = await httpService.post(
          "/orderpayment/saveOrderPayment",
          {
            order_id: savedOrder._id,
            client_id: cartPayload.vendor_client_id || null,
            is_pos_order: true,
            paymentType: payload.method,
            paid_amount: payload.amount,
            waived_amount: payload.adjustmentAmount || 0,
            amount: payload.amount,
            adjustmentAmount: payload.adjustmentAmount || 0,
            balanceRefunded: payload.balanceRefunded || 0,
            overpaymentAction: payload.overpaymentAction || null,
            creditAmount: payload.creditAmount || 0,
            reference: payload.reference || "",
            notes: payload.notes || "",
            // roundOff: payload.roundOff || 0,
            paymentStatus: payload.payment_status,
            subtotal_amount: cartPayload.amount_without_tax,
            gst: cartPayload.total_gst,
            discount: cartPayload.discount_amount || 0,
            amount_to_be_paid: cartPayload.total_amount,
            receipt_pdf_url: payload.receipt_pdf_url || null,
          },
        );
        if (!paymentRes?.status)
          return { success: false, error: "Order saved but payment failed" };
        return { ...paymentRes, data: savedOrder };
      }

      const response = await httpService.post(
        "/orderpayment/saveOrderPayment",
        {
          order_id: orderId,
          client_id: orderData?.vendor_client_id || null,
          is_pos_order: orderData?.is_pos_order || false,
          paymentType: payload.method,
          paid_amount: payload.amount,
          waived_amount: payload.adjustmentAmount || 0,
          amount: payload.amount,
          adjustmentAmount: payload.adjustmentAmount || 0,
          balanceRefunded: payload.balanceRefunded || 0,
          overpaymentAction: payload.overpaymentAction || null,
          creditAmount: payload.creditAmount || 0,
          reference: payload.reference || "",
          notes: payload.notes || "",
          // roundOff: payload.roundOff || 0,
          paymentStatus: payload.payment_status,
          subtotal_amount: totals.subtotal,
          gst: totals.gst,
          discount: totals.discount,
          amount_to_be_paid: totals.net,
          receipt_pdf_url: payload.receipt_pdf_url || null,
        },
      );
      return response;
    } catch (error) {
      console.error(error);
      return {
        success: false,
        error: error.response?.data?.message || "Payment failed",
      };
    }
  };

  const handlePaymentComplete = async () => {
    const amountNum = collectedAmount;
    const applied = Math.min(amountNum, effectiveDue);
    const excess = amountNum - applied;

    if (!paymentDetails.method) { toast.error("Select a payment method"); return; }
    if (amountNum <= 0) { toast.error("Enter a valid payment amount"); return; }
    if (paymentDetails.method !== "cash" && !paymentDetails.reference.trim()) {
      toast.error("Please enter a reference number for this payment method."); return;
    }
    if (isOverpayment && !overpaymentAction) {
      toast.error("Please choose how to handle the overpayment (Refund or Credit)."); return;
    }

    const paymentStatus = applied >= effectiveDue ? "paid" : applied > 0 ? "partial" : "unpaid";
    const paymentType = applied >= effectiveDue ? "full" : "partial";
    const hasClientId = orderData?.vendor_client_id || cartPayload?.vendor_client_id;
    if (applied < effectiveDue && !hasClientId) {
      toast.error("Partial payment requires client details. Please add client information first.");
      return;
    }

    setIsPaymentLoading(true);

    // ── STEP 1: For new POS sales, create order first to get real order number ──
    let savedOrder = null;
    if (cartPayload && !orderId) {
      const orderRes = await httpService.post(
        "/vendororder/addVendorOrderOrUpdate",
        { ...cartPayload, payment_status: paymentStatus },
      );
      if (!orderRes?.status) {
        toast.error("Failed to save order");
        setIsPaymentLoading(false);
        return;
      }
      savedOrder = orderRes.data;
    }

    // ── STEP 2: Set state so hidden PDF div re-renders with real order number ──
    setSavedOrderForPdf(savedOrder || orderData);

    // ── STEP 3: Wait for React to re-render ──
    await new Promise((resolve) => setTimeout(resolve, 300));

    // ── STEP 4: Generate and upload PDF (now has correct order number) ──
    setPdfUploadStatus("uploading");
    let receiptUrl = null;
    try {
      receiptUrl = await generateAndUploadReceiptPDF();
      setPdfUploadStatus("success");
    } catch (error) {
      console.error("Receipt PDF generation/upload failed:", error);
      setPdfUploadStatus("failed");
      toast.error("Failed to create receipt PDF.");
      setIsPaymentLoading(false);
      return;
    }

    // ── STEP 5: Save payment WITH receipt URL ──
    let apiResult;

    if (cartPayload && !orderId && savedOrder) {
      // Order already created — just save payment with receipt URL
      const paymentRes = await httpService.post("/orderpayment/saveOrderPayment", {
        order_id: savedOrder._id,
        client_id: cartPayload.vendor_client_id || null,
        is_pos_order: true,
        paymentType: paymentDetails.method,
        method: paymentDetails.method,
        paid_amount: applied,
        waived_amount: adjustmentAmount || 0,
        amount: applied,
        adjustmentAmount: adjustmentAmount || 0,
        balanceRefunded: overpaymentAction === "refund" ? excess : 0,
        overpaymentAction: overpaymentAction || null,
        creditAmount: overpaymentAction === "credit" ? excess : 0,
        reference: paymentDetails.reference || "",
        notes: paymentDetails.notes || "",
        roundOff: roundOffDifference,
        paymentStatus: paymentStatus,
        subtotal_amount: cartPayload.amount_without_tax,
        gst: cartPayload.total_gst,
        discount: cartPayload.discount_amount || 0,
        amount_to_be_paid: cartPayload.total_amount,
        receipt_pdf_url: receiptUrl,  // ← real URL
      });
      apiResult = { ...paymentRes, data: savedOrder };
    } else {
      // Existing order
      const payload = {
        order_id: orderId,
        client_id: orderData?.vendor_client_id ?? null,
        paymentType,
        amount: applied,
        excessAmount: excess,
        overpaymentAction,
        creditAmount: overpaymentAction === "credit" ? excess : 0,
        balanceRefunded: overpaymentAction === "refund" ? excess : 0,
        method: paymentDetails.method,
        reference: paymentDetails.reference,
        notes: paymentDetails.notes,
        adjustmentAmount,
        roundOff: roundOffDifference,
        totals,
        items,
        payment_status: paymentStatus,
        receipt_pdf_url: receiptUrl,  // ← real URL
      };
      apiResult = await submitPaymentToAPI(payload);
    }

    setIsPaymentLoading(false);

    if (apiResult?.status) {
      toast.success("Payment completed successfully!");
      paymentSuccessDataRef.current = apiResult.data;
      const receiptPayload = {
        method: paymentDetails.method,
        reference: paymentDetails.reference,
        notes: paymentDetails.notes,
        amount: applied,
        balanceRefunded: overpaymentAction === "refund" ? excess : 0,
        creditAmount: overpaymentAction === "credit" ? excess : 0,
      };
      const receipt = buildReceiptData(receiptPayload, apiResult.data);
      setReceiptData(receipt);
      setShowReceipt(true);
      setPaymentDetails({ amount: "", method: "", reference: "", notes: "" });
      setAdjustmentAmount(0);
      setOverpaymentAction(null);
      setPdfUploadStatus(null);
    } else {
      toast.error(apiResult?.error || "Payment failed");
    }
  };

  const buildReceiptData = (paymentPayload, savedOrder = null) => {
    // Use savedOrder for new POS sales, fall back to orderData for existing orders
    const orderInfo = savedOrder || orderData;

    const mappedItems = items.map((item) => ({
      name: item.service_name || item.product_details?.name || item.name || "Product",
      quantity: item.quantity || 1,
      price: item.unit_price || item.price || 0,
      total: item.final_total || item.total_price || item.total_amount || item.amount || 0,
      addons: item.addons,
      products: item.products,
      otherCharges: item.otherCharges,
    }));

    return {
      orderId: orderInfo?._id || orderId || paymentPayload.order_id,
      orderNumber: orderInfo?.order_number || orderData.order_number || orderData.orderNumber || "",
      date: new Date().toISOString(),
      customer: {
        name: `${orderInfo?.client_first_name || orderData?.client_first_name ||  ""} ${orderInfo?.client_last_name || orderData?.client_last_name || ""}`.trim()
              || (cartPayload?.vendor_client_id ? "Customer" : "Walk-in"),
        phone: orderInfo?.client_phone || orderData?.client_phone || cartPayload?.client_phone || "",
      },
      items: mappedItems,
      totals: {
        originalDue: originalDue,
        roundedDue: roundedDue,
        roundOffDifference: roundOffDifference,
        subtotal: totals.subtotal,
        discount: totals.discount,
        gst: totals.gst,
        net: totals.net,
        paid: totals.paid,
        adjustment: adjustmentAmount,
        dueAfterAdjustment: effectiveDue,
        amountPaid: paymentPayload.amount,
        balanceRefunded: paymentPayload.balanceRefunded || 0,
        creditApplied: paymentPayload.creditAmount || 0,
      },
      payment: {
        method: paymentPayload.method,
        reference: paymentPayload.reference,
        notes: paymentPayload.notes,
      },
    };
  };

  const handleCloseReceipt = () => {
    setShowReceipt(false);
    setReceiptData(null);

    if (paymentSuccessDataRef.current && onPaymentSuccess) {
      onPaymentSuccess(paymentSuccessDataRef.current);
      paymentSuccessDataRef.current = null;
    }

    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      setSavedOrderForPdf(null);
      setAdjustmentAmount(0);
      setPaymentDetails({ amount: "", method: "", reference: "", notes: "" });
      setOverpaymentAction(null);
      setShowOverpaymentPrompt(false);
      setPdfUploadStatus(null);
      paymentSuccessDataRef.current = null;
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;
  if (loadingOrder) {
    return (
      <div className="fixed inset-0 z-[1000] bg-black bg-opacity-40 flex items-center justify-center">
        <div className="bg-white p-6 rounded-xl shadow-xl">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="fixed inset-0 z-[1000] transition-all duration-300 ease-in-out">
        <div
          className="absolute inset-0 bg-black bg-opacity-40 backdrop-blur-[2px]"
          onClick={onClose}
        />
        <div
          className={`absolute right-0 top-0 h-full w-full ${!isFullWidth ? "lg:w-2/5" : ""} bg-white shadow-xl flex flex-col`}
        >
          {/* Header */}
          <div className="flex items-center p-3 border-b bg-gradient-to-r from-green-50 to-emerald-50 gap-2">
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full hover:bg-white/70 flex items-center justify-center"
            >
              <FiArrowLeft className="w-5 h-5 text-gray-700" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 flex items-center justify-center">
                <MdPayment className="w-3.5 h-3.5 text-white" />
              </div>
              <h2 className="text-lg font-semibold text-gray-800">
                {orderData?.order_number || "New Order"} Payment Details
              </h2>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto p-4 space-y-3">
            {/* Totals Section */}
            <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 space-y-1 text-sm">
              <div className="flex justify-between items-start font-semibold pt-1">
                <div className="flex items-start gap-2">
                  <span>Order Amount</span>
                  <div className="relative group">
                    <FiInfo className="w-4 h-4 text-gray-500 cursor-pointer mt-0.5" />
                    <div className="absolute left-6 top-0 z-20 hidden group-hover:block w-64 bg-white border border-gray-200 shadow-xl rounded-lg p-3 text-xs font-normal text-gray-700">
                      <div className="flex justify-between mb-1">
                        <span>Subtotal</span>
                        <span>{formatCurrency(totals.subtotal, 0)}</span>
                      </div>
                      {!orderData?.is_pos_order && totals.discount > 0 && (
                        <div className="flex justify-between mb-1 text-red-600">
                          <span>Discount</span>
                          <span>- {formatCurrency(totals.discount, 0)}</span>
                        </div>
                      )}
                      <div className="flex justify-between mb-1 text-green-600">
                        <span>GST</span>
                        <span>+ {formatCurrency(totals.gst, 0)}</span>
                      </div>
                      <div className="border-t pt-1 mt-1 flex justify-between font-semibold text-gray-900">
                        <span>Total</span>
                        <span>{formatCurrency(totals.net, 0)}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <span>{formatCurrency(totals.net, 0)}</span>
              </div>
              {totals.paid > 0 && (
                <div className="flex justify-between text-blue-600">
                  <span>Amount Already Paid</span>
                  <span>- {formatCurrency(totals.paid, 0)}</span>
                </div>
              )}
            </div>

            {/* Savings Message */}
            {totals.discount > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm font-medium text-green-700">
                  🎉 Customer saved{" "}
                  <span className="font-bold">{formatCurrency(totals.discount, 0)}</span> on this order
                </p>
              </div>
            )}

            <div className="flex gap-2">
              <div className="flex-1 bg-yellow-50 p-2 rounded-lg border border-yellow-200">
                <label className="block text-sm font-medium mb-1">
                  Adjustment Amount
                </label>
                <input
                  type="number"
                  value={adjustmentAmount}
                  onChange={(e) =>
                    setAdjustmentAmount(
                      Math.max(0, parseFloat(e.target.value) || 0),
                    )
                  }
                  min="0"
                  step="0.01"
                  placeholder="Enter adjustment amount"
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-0.5">
                  Subtracted from rounded due
                </p>
              </div>
              <div className="flex-1 flex flex-col bg-green-50 p-2 rounded-lg border border-green-200 justify-center items-center">
                <div className="text-xs font-medium text-green-800">
                  Final Amount to Pay
                </div>
                <div className="text-xl font-bold text-green-700">
                  {formatCurrency(effectiveDue, 0)}
                </div>
              </div>
            </div>

            {/* Payment Method */}
            <div>
              <label className="block text-sm font-medium mb-2">Payment Method</label>
              <div className="grid grid-cols-3 gap-2">
                {paymentMethodOptions.map((option) => (
                  <label
                    key={option.value}
                    className={`
                      flex items-center justify-center gap-2 p-2 rounded-lg border cursor-pointer transition-all
                      ${paymentDetails.method === option.value
                        ? "border-green-500 bg-green-50 ring-1 ring-green-200"
                        : "border-gray-200 bg-white hover:border-gray-300"
                      }
                    `}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value={option.value}
                      checked={paymentDetails.method === option.value}
                      onChange={() => handlePaymentMethodChange(option.value)}
                      className="w-3.5 h-3.5 text-green-600 focus:ring-green-500"
                    />
                    <span className="text-xs font-medium text-gray-700">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Payment Amount Input */}
            {paymentDetails.method && (
              <div>
                <label className="block text-sm font-medium mb-1">Amount Collected</label>
                <input
                  type="number"
                  value={paymentDetails.amount}
                  onChange={(e) =>
                    setPaymentDetails((prev) => ({ ...prev, amount: e.target.value }))
                  }
                  placeholder="Enter amount received"
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg"
                  step="0.01"
                  min="0"
                />
              </div>
            )}

            {/* Overpayment Prompt */}
            {showOverpaymentPrompt && (
              <div className="p-2 border border-yellow-300 bg-yellow-50 rounded-lg text-sm">
                <p className="font-medium text-yellow-800 mb-1">
                  Amount exceeds due. How to handle excess?
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setOverpaymentAction("refund")}
                    className={`px-2 py-0.5 text-sm rounded ${overpaymentAction === "refund" ? "bg-blue-600 text-white" : "bg-gray-200"}`}
                  >
                    Refund
                  </button>
                  <button
                    onClick={() => setOverpaymentAction("credit")}
                    className={`px-2 py-0.5 text-sm rounded ${overpaymentAction === "credit" ? "bg-blue-600 text-white" : "bg-gray-200"}`}
                  >
                    Add as Credit
                  </button>
                </div>
                {overpaymentAction && (
                  <p className="text-xs mt-1">
                    Excess: {formatCurrency(collectedAmount - effectiveDue, 0)}{" "}
                    will be {overpaymentAction === "refund" ? "refunded" : "credited"}.
                  </p>
                )}
              </div>
            )}

            {/* Reference for non-cash */}
            {paymentDetails.method && paymentDetails.method !== "cash" && (
              <div>
                <label className="block text-sm font-medium mb-1">
                  Reference Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={paymentDetails.reference}
                  onChange={(e) =>
                    setPaymentDetails((prev) => ({
                      ...prev,
                      reference: e.target.value,
                    }))
                  }
                  placeholder={
                    paymentDetails.method === "card"
                      ? "Card Reference / Auth Code"
                      : "Transaction ID"
                  }
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg"
                />
              </div>
            )}
          </div>

          {/* Footer Buttons */}
          <div className="p-3 border-t bg-white space-y-2">
            {pdfUploadStatus && (
              <div className="text-sm flex items-center gap-2">
                {pdfUploadStatus === "uploading" && (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                    <span className="text-blue-600">Uploading receipt...</span>
                  </>
                )}
                {pdfUploadStatus === "success" && (
                  <span className="text-green-600">✓ Receipt uploaded</span>
                )}
                {pdfUploadStatus === "failed" && (
                  <span className="text-red-600">✗ Receipt upload failed</span>
                )}
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handlePaymentComplete}
                disabled={
                  !paymentDetails.method ||
                  isPaymentLoading ||
                  !paymentDetails.amount ||
                  (isOverpayment && !overpaymentAction) ||
                  pdfUploadStatus === "uploading"
                }
                className="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium text-sm flex items-center justify-center gap-2"
              >
                {isPaymentLoading ? (
                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                ) : (
                  <FiCheck className="w-4 h-4" />
                )}
                {isPaymentLoading ? "Processing..." : "Complete Payment"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden receipt for PDF generation – now using a table-based structure matching PaymentReceipt */}
      <div style={{ display: "none" }}>
        <div
          id="receipt-content-pdf"
          style={{
            background: "#fff",
            maxWidth: "720px",
            margin: "auto",
            padding: "16px",
            border: "1px solid #ddd",
            fontFamily: "Arial, sans-serif",
            color: "#000",
            boxSizing: "border-box",
          }}
        >
          {/* ----- Header ----- */}
          <table
            width="100%"
            cellPadding={0}
            cellSpacing={0}
            style={{
              fontSize: "14px",
              color: "#000",
              marginBottom: "16px",
            }}
          >
            <tbody>
              <tr>
                <td style={{ paddingBottom: "14px" }}>
                  <h2
                    style={{
                      margin: 0,
                      fontSize: "24px",
                      fontWeight: "700",
                      letterSpacing: "0.5px",
                    }}
                  >
                    RECEIPT
                  </h2>
                </td>
              </tr>

              <tr>
                <td style={{ padding: "4px 0", lineHeight: "20px" }}>
                  <strong>Order #:</strong>{" "}
                  {savedOrderForPdf?.order_number || orderData?.order_number || "New Order"}
                </td>
              </tr>

              <tr>
                <td style={{ padding: "4px 0", lineHeight: "20px" }}>
                  <strong>Date:</strong>{" "}
                  {new Date().toLocaleString("en-IN", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  })}
                </td>
              </tr>

              <tr>
                <td style={{ padding: "4px 0", lineHeight: "20px" }}>
                  <strong>Customer:</strong>{" "}
                  {`${savedOrderForPdf?.client_first_name || orderData?.client_first_name || cartPayload?.client_first_name || ""} ${
                    savedOrderForPdf?.client_last_name || orderData?.client_last_name || cartPayload?.client_last_name || ""
                  }`.trim()}
                  {(savedOrderForPdf?.client_phone || orderData?.client_phone || cartPayload?.client_phone)
                    ? ` (${savedOrderForPdf?.client_phone || orderData?.client_phone || cartPayload?.client_phone})`
                    : ""}
                </td>
              </tr>
            </tbody>
          </table>

          {/* ----- Items Table ----- */}
          <table
            width="100%"
            cellPadding={0}
            cellSpacing={0}
            style={{
              borderCollapse: "collapse",
              fontSize: "13px",
              marginBottom: "16px",
              tableLayout: "fixed",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#f5f5f5" }}>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "10px",
                    textAlign: "left",
                    width: "60%",
                  }}
                >
                  Item
                </th>

                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "10px",
                    textAlign: "center",
                    width: "12%",
                  }}
                >
                  Qty
                </th>

                {/* <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "10px",
                    textAlign: "right",
                    width: "23%",
                  }}
                >
                  Price
                </th> */}

                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "10px",
                    textAlign: "right",
                    width: "28%",
                  }}
                >
                  Total
                </th>
              </tr>
            </thead>

            {/* <tbody>
              {items.map((item, idx) => (
                <tr key={idx}>
                  <td
                    style={{
                      border: "1px solid #ddd",
                      padding: "10px",
                      lineHeight: "18px",
                      wordBreak: "break-word",
                    }}
                  >
                    {item.service_name}
                  </td>

                  <td
                    style={{
                      border: "1px solid #ddd",
                      padding: "10px",
                      textAlign: "center",
                    }}
                  >
                    {item.quantity || 1}
                  </td>

                  <td
                    style={{
                      border: "1px solid #ddd",
                      padding: "10px",
                      textAlign: "right",
                      whiteSpace: "nowrap",
                    }}
                  >
                    ₹{Number(item.unit_price || item.price || item.base_total || 0).toFixed(2)}
                  </td>

                  <td
                    style={{
                      border: "1px solid #ddd",
                      padding: "10px",
                      textAlign: "right",
                      whiteSpace: "nowrap",
                    }}
                  >
                    ₹
                    {Number(item.final_total || item.total_price || 0).toFixed(2)
                    }
                  </td>
                </tr>
              ))}
            </tbody> */}
            <tbody>
              {items.map((item, idx) => (
                <tr key={idx}>
                  <td
                    style={{
                      border: "1px solid #ddd",
                      padding: "10px",
                      lineHeight: "18px",
                      wordBreak: "break-word",
                    }}
                  >
                    {/* Main item name */}
                    <div style={{ fontWeight: "bold" }}>{item.service_name}</div>

                    {/* Addons (indented) */}
                    {item.addons?.map((addon, aIdx) => (
                      <div key={`addon-${aIdx}`} style={{ paddingLeft: "20px", fontSize: "12px", color: "#555" }}>
                        • {addon.name} – ₹{addon.addon_price.toFixed(2)}
                      </div>
                    ))}
                    
                    {/* Addons (indented) */}
                    {item.products?.map((product, aIdx) => (
                      <div key={`product-${aIdx}`} style={{ paddingLeft: "20px", fontSize: "12px", color: "#555" }}>
                        • {product.name} x {product.quantity} – ₹{product.total_price.toFixed(2)}
                      </div>
                    ))}

                    {/* Other charges (indented) */}
                    {item.otherCharges?.map((charge, cIdx) => (
                      <div key={`charge-${cIdx}`} style={{ paddingLeft: "20px", fontSize: "12px", color: "#555" }}>
                        • {charge.description} – ₹{charge.amount.toFixed(2)}
                      </div>
                    ))}
                  </td>

                  <td style={{ border: "1px solid #ddd", padding: "10px", textAlign: "center" }}>
                    {item.quantity || 1}{item.item_type=="boarding" ? `(Days)` : ``}
                  </td>

                  {/* <td style={{ border: "1px solid #ddd", padding: "10px", textAlign: "right", whiteSpace: "nowrap" }}>
                    ₹{(item.unit_price || item.price || 0).toFixed(2)}
                  </td> */}

                  <td style={{ border: "1px solid #ddd", padding: "10px", textAlign: "right", whiteSpace: "nowrap" }}>
                    ₹{(item.final_total || item.total_price || 0).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* ----- Totals ----- */}
          <table
            width="100%"
            cellPadding={0}
            cellSpacing={0}
            style={{
              fontSize: "14px",
              marginBottom: "8px",
              borderCollapse: "collapse",
            }}
          >
            <tbody>
              <tr>
                <td
                  style={{
                    width: "70%",
                    padding: "4px 0",
                    lineHeight: "20px",
                  }}
                >
                  Subtotal:
                </td>

                <td
                  style={{
                    textAlign: "right",
                    padding: "4px 0",
                    whiteSpace: "nowrap",
                    lineHeight: "20px",
                  }}
                >
                  ₹{totals.subtotal.toFixed(2)}
                </td>
              </tr>

              {totals.discount > 0 && (
                <tr>
                  <td
                    style={{
                      color: "red",
                      padding: "4px 0",
                      lineHeight: "20px",
                    }}
                  >
                    Discount:
                  </td>

                  <td
                    style={{
                      textAlign: "right",
                      color: "red",
                      padding: "4px 0",
                      whiteSpace: "nowrap",
                      lineHeight: "20px",
                    }}
                  >
                    -₹{totals.discount.toFixed(2)}
                  </td>
                </tr>
              )}

              <tr>
                <td
                  style={{
                    color: "green",
                    padding: "4px 0",
                    lineHeight: "20px",
                  }}
                >
                  GST:
                </td>

                <td
                  style={{
                    textAlign: "right",
                    color: "green",
                    padding: "4px 0",
                    whiteSpace: "nowrap",
                    lineHeight: "20px",
                  }}
                >
                  +₹{totals.gst.toFixed(2)}
                </td>
              </tr>

              <tr className=" mt-3 mb-3">
                <td
                  colSpan={2}
                  style={{
                    borderTop: "1px solid #ccc",
                    margin: "4px 0",
                  }}
                />
              </tr>

              <tr>
                <td
                  style={{
                    fontWeight: "600",
                    padding: "4px 0",
                    lineHeight: "20px",
                  }}
                >
                  Order Total:
                </td>

                <td
                  style={{
                    textAlign: "right",
                    fontWeight: "600",
                    padding: "4px 0",
                    whiteSpace: "nowrap",
                    lineHeight: "20px",
                  }}
                >
                  ₹{totals.net.toFixed(2)}
                </td>
              </tr>

              {totals.paid > 0 && (
                <tr>
                  <td
                    style={{
                      color: "blue",
                      padding: "4px 0",
                      lineHeight: "20px",
                    }}
                  >
                    Already Paid:
                  </td>

                  <td
                    style={{
                      textAlign: "right",
                      color: "blue",
                      padding: "4px 0",
                      whiteSpace: "nowrap",
                      lineHeight: "20px",
                    }}
                  >
                    -₹{totals.paid.toFixed(2)}
                  </td>
                </tr>
              )}

              {adjustmentAmount > 0 && (
                <tr>
                  <td
                    style={{
                      color: "#b8860b",
                      padding: "4px 0",
                      lineHeight: "20px",
                    }}
                  >
                    Adjustment:
                  </td>

                  <td
                    style={{
                      textAlign: "right",
                      color: "#b8860b",
                      padding: "4px 0",
                      whiteSpace: "nowrap",
                      lineHeight: "20px",
                    }}
                  >
                    -₹{adjustmentAmount.toFixed(2)}
                  </td>
                </tr>
              )}

              <tr className=" mt-3 mb-3">
                <td
                  colSpan={2}
                  style={{
                    borderTop: "2px solid #000",
                    margin: "4px 0",
                  }}
                />
              </tr>

              <tr>
                <td
                  style={{
                    fontWeight: "600",
                    padding: "4px 0",
                    lineHeight: "20px",
                  }}
                >
                  Amount Payable:
                </td>

                <td
                  style={{
                    textAlign: "right",
                    fontWeight: "700",
                    fontSize: "16px",
                    padding: "4px 0",
                    whiteSpace: "nowrap",
                    lineHeight: "20px",
                  }}
                >
                  ₹{effectiveDue.toFixed(0)}
                </td>
              </tr>

              <tr>
                <td
                  style={{
                    padding: "4px 0",
                    lineHeight: "20px",
                  }}
                >
                  Amount Received:
                </td>

                <td
                  style={{
                    textAlign: "right",
                    padding: "4px 0",
                    whiteSpace: "nowrap",
                    lineHeight: "20px",
                  }}
                >
                  ₹{collectedAmount.toFixed(0)}
                </td>
              </tr>

              {overpaymentAction === "refund" &&
                collectedAmount - effectiveDue > 0 && (
                  <tr>
                    <td
                      style={{
                        color: "red",
                        padding: "4px 0",
                        lineHeight: "20px",
                      }}
                    >
                      Refunded:
                    </td>

                    <td
                      style={{
                        textAlign: "right",
                        color: "red",
                        padding: "4px 0",
                        whiteSpace: "nowrap",
                        lineHeight: "20px",
                      }}
                    >
                      ₹{(collectedAmount - effectiveDue).toFixed(0)}
                    </td>
                  </tr>
                )}

              {overpaymentAction === "credit" &&
                collectedAmount - effectiveDue > 0 && (
                  <tr>
                    <td
                      style={{
                        color: "blue",
                        padding: "4px 0",
                        lineHeight: "20px",
                      }}
                    >
                      Credit Added:
                    </td>

                    <td
                      style={{
                        textAlign: "right",
                        color: "blue",
                        padding: "4px 0",
                        whiteSpace: "nowrap",
                        lineHeight: "20px",
                      }}
                    >
                      ₹{(collectedAmount - effectiveDue).toFixed(0)}
                    </td>
                  </tr>
                )}
            </tbody>
          </table>

          {/* ----- Payment Details ----- */}
          <table
            width="100%"
            cellPadding={0}
            cellSpacing={0}
            style={{
              fontSize: "14px",
              borderTop: "1px solid #ddd",
              marginTop: "8px",
            }}
          >
            <tbody>
              <tr>
                <td
                  style={{
                    paddingTop: "10px",
                    fontWeight: "600",
                  }}
                >
                  Payment Method:
                </td>

                <td
                  style={{
                    textAlign: "right",
                    paddingTop: "10px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {paymentDetails.method?.toUpperCase()}
                  {paymentDetails.reference
                    ? ` (${paymentDetails.reference})`
                    : ""}
                </td>
              </tr>

              {paymentDetails.notes && (
                <tr>
                  <td
                    style={{
                      paddingTop: "6px",
                      fontWeight: "600",
                    }}
                  >
                    Notes:
                  </td>

                  <td
                    style={{
                      textAlign: "right",
                      paddingTop: "6px",
                    }}
                  >
                    {paymentDetails.notes}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Receipt Modal (UI only) */}
      <PaymentReceipt
        isOpen={showReceipt && !!receiptData}
        onClose={handleCloseReceipt}
        receiptData={receiptData}
      />
    </>
  );
};

export default OrderPaymentDrawer;
